import { execute } from '@lib/graphql/execute'
import { graphql } from '@lib/graphql/gql'
import { MutationConfig } from '@lib/react-query'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import { getCustomerQueryOptions } from './get-customer'

export const customerUpdateFormSchema = z.object({
  firstName: z.string().min(1, { message: 'First name is required' }),
  lastName: z.string().min(1, { message: 'Last name is required' }),
  preferences: z.array(z.object({ key: z.string(), value: z.string() })),

  newsletterSignup: z.object({
    value: z.boolean(),
    changed: z.boolean(),
  }),
})

export type CustomerUpdateFormValues = z.infer<typeof customerUpdateFormSchema>

const customerUpdateMutationDocument = graphql(`
  mutation CustomerUpdate($input: CustomerUpdateInput!) {
    customerUpdate(input: $input) {
      customer {
        firstName
        lastName
        metadata {
          key
          value
        }
      }
    }
  }
`)

export const customerUpdate = ({
  data,
}: {
  data: CustomerUpdateFormValues
}) => {
  return execute(customerUpdateMutationDocument as any, {
    input: {
      firstName: data.firstName,
      lastName: data.lastName,
      preferences: data.preferences,
    },
  })
}

type UseCustomerUpdateOptions = {
  mutationConfig?: MutationConfig<typeof customerUpdate>
}

export const useCustomerUpdate = ({
  mutationConfig,
}: UseCustomerUpdateOptions = {}) => {
  const queryClient = useQueryClient()

  const { onSuccess, ...restConfig } = mutationConfig || {}

  return useMutation({
    onSuccess: (...args) => {
      queryClient.invalidateQueries({
        queryKey: getCustomerQueryOptions().queryKey,
      })
      onSuccess?.(...args)
    },
    ...restConfig,
    mutationFn: customerUpdate,
  })
}
